import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { VoleiProtocolService } from '../../../services/volei-protocol.service';
import { VoleiProtocol } from '../../../models/voleiprotocol';

// Interface for team stats within a set
interface TeamSetStats {
  t: number | null;
  s: number | null;
  v: number | null; // Assuming V is 0 or 1 based on input type="number" maxlength="1"
  p: number | null;
}

// Interface for a single set's data
interface SetData {
  setNumber: number;
  teamA: TeamSetStats;
  teamB: TeamSetStats;
  duration: number | null; // Duration in minutes
  played: boolean; // Helper to track if set was played
}

// Interface for team total stats
interface TeamTotalStats {
  t: number;
  s: number;
  v: number;
  p: number;
}

@Component({
  selector: 'app-volei-protocol',
  templateUrl: './volei-protocol.component.html',
  styleUrls: ['./volei-protocol.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VoleiProtocolComponent implements OnInit {
  public voleiProtocols: any[] = [];
  public voleiProtocolSelectec: any[];
  public gameTime: string = "";
  public itemProtocol: VoleiProtocol;
  public isLoaded: boolean = false;
  public refereeType: number = 1;

  readonly STORAGE_KEY_START_TIME = 'gameStartTime';

  sets: SetData[] = [];
  readonly MAX_SETS = 5;
  readonly BREAK_TIME_MINUTES = 3;

  teamATotals: TeamTotalStats = { t: 0, s: 0, v: 0, p: 0 };
  teamBTotals: TeamTotalStats = { t: 0, s: 0, v: 0, p: 0 };

  totalSetDurationMinutes: number = 0;
  totalBreakTimeMinutes: number = 0;
  totalGameDurationMinutes: number = 0;
  totalGameDurationFormatted: string = '0h 0m';
  playedSetsCount: number = 0;

  gameStartTime: string = ''; // Format: HH:MM
  gameEndTime: string = '';   // Format: HH:MM (Calculated)


  constructor(private voleiProtocolService: VoleiProtocolService) {

  }

  ngOnInit() {
    this.loadStartTimeFromStorage();
    this.initializeSets();
    this.isLoaded = false;

    this.voleiProtocolService.get().subscribe((res: any[]) => {
      this.voleiProtocols = res;
      this.isLoaded = true;
    })
  }

  loadStartTimeFromStorage(): void {
    try {
      const savedTime = localStorage.getItem(this.STORAGE_KEY_START_TIME);
      if (savedTime) { // Check if a value was found
        this.gameStartTime = savedTime;
      } else {
        // Optional: Set a default if nothing is saved, or keep it empty
        // this.gameStartTime = '14:00';
      }
    } catch (e) {
      console.error("Error reading gameStartTime from localStorage:", e);
      // Handle potential errors (e.g., storage disabled in private mode)
    }
  }


  initializeSets(): void {
    this.sets = [];
    for (let i = 1; i <= this.MAX_SETS; i++) {
      this.sets.push({
        setNumber: i,
        teamA: { t: null, s: null, v: null, p: null },
        teamB: { t: null, s: null, v: null, p: null },
        duration: null,
        played: false
      });
    }
    // Optionally set default start time or load from somewhere
    // this.gameStartTime = '14:00';
    this.calculateAll(); // Initial calculation
  }

  // --- Main Calculation Trigger ---
  onDataChange(): void {
    this.saveStartTimeToStorage(); // Save the start time whenever any data changes
    this.calculateAll();           // Then perform all calculations
  }

  saveStartTimeToStorage(): void {
    try {
      // Only save if gameStartTime has a value, or save empty string if cleared
      localStorage.setItem(this.STORAGE_KEY_START_TIME, this.gameStartTime || '');
    } catch (e) {
      console.error("Error saving gameStartTime to localStorage:", e);
      // Handle potential errors (e.g., storage quota exceeded)
    }
  }

  calculateAll(): void {
    this.calculateSetPlayedStatus();
    this.calculateTotals();
    this.calculateDurations();
    this.calculateEndTime();
  }

  // --- Calculation Steps ---

  calculateSetPlayedStatus(): void {
    this.playedSetsCount = 0;
    this.sets.forEach(set => {
      // A set is played if it has a duration > 0 OR if points were scored
      // Using duration > 0 is simpler if duration is always entered for played sets
      set.played = (set.duration ?? 0) > 0;
      // Alternative/More Robust: Check points too
      // set.played = (set.duration ?? 0) > 0 || (set.teamA.p ?? 0) > 0 || (set.teamB.p ?? 0) > 0;
      if (set.played) {
        this.playedSetsCount++;
      }
    });
  }


  calculateTotals(): void {
    // Reset totals
    this.teamATotals = { t: 0, s: 0, v: 0, p: 0 };
    this.teamBTotals = { t: 0, s: 0, v: 0, p: 0 };

    this.sets.forEach(set => {
      if (set.played) { // Only sum stats for sets that were played
        this.teamATotals.t += set.teamA.t ?? 0;
        this.teamATotals.s += set.teamA.s ?? 0;
        this.teamATotals.v += set.teamA.v ?? 0;
        this.teamATotals.p += set.teamA.p ?? 0;

        this.teamBTotals.t += set.teamB.t ?? 0;
        this.teamBTotals.s += set.teamB.s ?? 0;
        this.teamBTotals.v += set.teamB.v ?? 0;
        this.teamBTotals.p += set.teamB.p ?? 0;
      }
    });
  }

  calculateDurations(): void {
    this.totalSetDurationMinutes = 0;
    this.sets.forEach(set => {
      if (set.played) {
        this.totalSetDurationMinutes += set.duration ?? 0;
      }
    });

    // Calculate break time: (played sets - 1) * break_time
    this.totalBreakTimeMinutes = this.playedSetsCount > 1
      ? (this.playedSetsCount - 1) * this.BREAK_TIME_MINUTES
      : 0;

    // Calculate total game duration
    this.totalGameDurationMinutes = this.totalSetDurationMinutes + this.totalBreakTimeMinutes;

    // Format total duration
    this.totalGameDurationFormatted = this.formatDuration(this.totalGameDurationMinutes);
  }

  calculateEndTime(): void {
    if (!this.gameStartTime || this.totalGameDurationMinutes <= 0) {
      this.gameEndTime = '';
      return;
    }

    try {
      const [startHours, startMinutes] = this.gameStartTime.split(':').map(Number);
      if (isNaN(startHours) || isNaN(startMinutes)) {
        this.gameEndTime = 'Invalid Start';
        return;
      }

      const startDate = new Date();
      startDate.setHours(startHours, startMinutes, 0, 0); // Set start time on a temporary date object

      const endDate = new Date(startDate.getTime() + this.totalGameDurationMinutes * 60000); // Add duration in milliseconds

      const endHours = endDate.getHours().toString().padStart(2, '0');
      const endMinutes = endDate.getMinutes().toString().padStart(2, '0');

      this.gameEndTime = `${endHours}:${endMinutes}`;

    } catch (e) {
      console.error("Error calculating end time:", e);
      this.gameEndTime = 'Error';
    }
  }

  // --- Helper Functions ---

  formatDuration(totalMinutes: number): string {
    if (totalMinutes <= 0) {
      return '0h 0m';
    }
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  }

  // Helper for ngFor trackBy for performance if list gets very large (optional)
  trackBySetNumber(index: number, set: SetData): number {
    return set.setNumber;
  }

  toTime(timeString: string, time: number) {
    var timeTokens = timeString.split(':');

    if (isNaN(Number(timeTokens[0])) || isNaN(Number(timeTokens[1]))) {
      return "00:00"
    } else {
      // Pegando a data atual
      let dataAtual = new Date();

      // Definindo a hora desejada (por exemplo, 16:00)
      let hora = Number(timeTokens[0]);
      let minuto = Number(timeTokens[1]);

      // Criando uma nova data com o dia atual e a hora especificada
      let novaData = new Date(
        dataAtual.getFullYear(), // Ano atual
        dataAtual.getMonth(), // Mês atual (0-11)
        dataAtual.getDate(), // Dia atual
        hora, // Hora desejada (16)
        minuto // Minutos desejados (00)
      );

      novaData.setSeconds(novaData.getSeconds() - time);

      let horas = novaData.getHours().toString().padStart(2, '0'); // Hora com zero à esquerda se necessário
      let minutos = novaData.getMinutes().toString().padStart(2, '0'); // Minuto com zero à esquerda se necessário

      let horaFormatada = `${horas}:${minutos}`;

      return horaFormatada;
    }
  }

  toMinutesStr(timeBefore: number): string {
    let retValue = timeBefore / 60;

    return "(" + (retValue < 0 ? 0 : retValue) + " minutos antes)";
  }

  get filteredVoleiProtocols(): any[] {
    return this.voleiProtocols.filter(x => (x.refereeType ?? 1) == this.refereeType);
  }

  openObservations(voleiProtocol: VoleiProtocol) {
    this.itemProtocol = voleiProtocol;
  }

}


